import React from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";

import invariant from "tiny-invariant";

import { alertActions } from "../../features/alert/alertSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { Breakpoint } from "../../hooks/useBreakpoint";
import { updateStandings } from "../../lib/api";
import { AUTH_LEVELS } from "../../lib/globals";

const IndividualTournamentSidebarButtons = ({
    breakpoint,
    toggleNavbar,
    routePrefix,
    tournament,
    setModalTournamentId,
    setModalAction,
    setShowModal,
}: {
    breakpoint: Breakpoint;
    toggleNavbar: () => void;
    routePrefix: string;
    tournament: number;
    setModalTournamentId: React.Dispatch<
        React.SetStateAction<number | undefined>
    >;
    setModalAction: React.Dispatch<
        React.SetStateAction<
            | "assign"
            | "reset"
            | "pool"
            | "clear"
            | "placeholder"
            | "clearplaceholder"
        >
    >;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const { user } = useAppSelector(state => state.auth);
    invariant(user);

    const masterTournaments = useAppSelector(
        state => state.tournamentmanagement.masterTournaments,
    );
    const isMasterTournament = masterTournaments.find(t => t.id === tournament);

    const dispatch = useAppDispatch();

    return (
        <>
            <Link
                to={`${routePrefix}/tournament/${tournament}/edit`}
                className="btn btn-primary"
                onClick={toggleNavbar}
            >
                Tournament Home
            </Link>
            <Link
                to={`${routePrefix}/tournament/${tournament}/calendar`}
                className="btn btn-primary"
                onClick={toggleNavbar}
            >
                Tournament Calendar
            </Link>
            <Link
                to={`${routePrefix}/tournament/${tournament}/schedule/pool`}
                className="btn btn-primary"
                onClick={toggleNavbar}
            >
                Pool Play Schedule
            </Link>
            <Link
                to={`${routePrefix}/tournament/${tournament}/schedule/bracket`}
                className="btn btn-primary"
                onClick={toggleNavbar}
            >
                Bracket Schedule
            </Link>
            <Link
                to={`${routePrefix}/tournament/${tournament}/standings`}
                className="btn btn-primary"
                onClick={toggleNavbar}
            >
                Tournament Standings
            </Link>
            <Link
                to={`${routePrefix}/tournament/${tournament}/playoffs`}
                className="btn btn-primary"
                onClick={toggleNavbar}
            >
                Tournament Bracket
            </Link>
            <Button
                className="btn btn-primary"
                onClick={() => {
                    toggleNavbar();
                    setModalAction("assign");
                    setModalTournamentId(tournament);
                    setShowModal(true);
                }}
            >
                Assign Bracket Seeds
            </Button>
            {AUTH_LEVELS.sitemanagement.includes(user.userLevel) && (
                <React.Fragment>
                    <Button
                        className="btn btn-primary"
                        onClick={() => {
                            updateStandings(tournament, user.id);
                            dispatch(
                                alertActions.success({
                                    title: "Standings update in progress...",
                                    text: "Please wait a few seconds for it to complete",
                                    dismissible: true,
                                    persist: false,
                                }),
                            );
                        }}
                    >
                        Update Standings
                    </Button>
                    {isMasterTournament && (
                        <Link
                            to={`${routePrefix}/tournament/${tournament}/umpires`}
                            className="btn btn-primary"
                            onClick={toggleNavbar}
                        >
                            Umpire Schedule
                        </Link>
                    )}
                    {isMasterTournament && (
                        <Link
                            to={`${routePrefix}/tournament/${tournament}/schedule-actions`}
                            className="btn btn-primary"
                            onClick={toggleNavbar}
                        >
                            Schedule Actions
                        </Link>
                    )}
                    <Button
                        className="btn btn-primary"
                        onClick={() => {
                            toggleNavbar();
                            setModalAction("reset");
                            setModalTournamentId(tournament);
                            setShowModal(true);
                        }}
                    >
                        Reset Bracket Seeds
                    </Button>
                    {isMasterTournament && (
                        <Button
                            variant="warning"
                            onClick={() => {
                                toggleNavbar();
                                setModalAction("placeholder");
                                setModalTournamentId(tournament);
                                setShowModal(true);
                            }}
                        >
                            Generate Placeholder Games
                        </Button>
                    )}
                    {isMasterTournament && (
                        <Button
                            variant="warning"
                            onClick={() => {
                                toggleNavbar();
                                setModalAction("clearplaceholder");
                                setModalTournamentId(tournament);
                                setShowModal(true);
                            }}
                        >
                            Clear Placeholder Games
                        </Button>
                    )}
                    {isMasterTournament && (
                        <Button
                            variant="warning"
                            onClick={() => {
                                toggleNavbar();
                                setModalAction("pool");
                                setModalTournamentId(tournament);
                                setShowModal(true);
                            }}
                        >
                            Generate Tournament Schedule
                        </Button>
                    )}
                    {isMasterTournament &&
                        process.env.BUILD_ENV !== "production" && (
                            <Button
                                variant="warning"
                                onClick={() => {
                                    toggleNavbar();
                                    setModalAction("clear");
                                    setModalTournamentId(tournament);
                                    setShowModal(true);
                                }}
                            >
                                Clear Tournament Schedule
                            </Button>
                        )}
                </React.Fragment>
            )}
        </>
    );
};

export default IndividualTournamentSidebarButtons;
